/**
 * フライトログ一覧テーブル（/flight-logs ページと DeviceDetail で共用）
 * - thingName を固定すると機体単位、未指定なら組織横断（アップロード順）
 * - 日付範囲フィルタ、続きの読み込み、ダウンロード
 */

import React, { useState, useEffect, useCallback } from 'react';
import { flightLogApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Badge, Input, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '../components/ui';

const DATE_SOURCE_LABELS = { gps: 'GPS', filename: 'ファイル名', client: '申告', upload: 'アップロード時刻' };
const SOURCE_LABELS = { manual: { label: '手動', variant: 'default' }, device: { label: '機体', variant: 'info' } };

export const formatSize = (bytes) => {
  if (bytes === null || bytes === undefined) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const formatDateTime = (isoStr) => {
  if (!isoStr) return '-';
  return new Date(isoStr).toLocaleString('ja-JP', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

export default function FlightLogTable({ thingName = null, pageSize = 50, refreshKey = 0, compact = false, onError }) {
  const [logs, setLogs] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [applied, setApplied] = useState({ from: '', to: '' });
  const [downloading, setDownloading] = useState(null);

  const reportError = useCallback((msg) => { if (onError) onError(msg); }, [onError]);

  const fetchLogs = useCallback(async (token = null) => {
    if (token) setLoadingMore(true); else setLoading(true);
    try {
      const data = await flightLogApi.list({
        thingName: thingName || undefined,
        from: applied.from || undefined,
        to: applied.to || undefined,
        limit: pageSize,
        nextToken: token || undefined,
      });
      setLogs((prev) => (token ? [...prev, ...(data.logs || [])] : (data.logs || [])));
      setNextToken(data.nextToken || null);
    } catch (err) {
      reportError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [thingName, applied, pageSize, reportError]);

  useEffect(() => { fetchLogs(null); }, [fetchLogs, refreshKey]);

  const handleDownload = async (log) => {
    setDownloading(log.logId);
    try {
      await flightLogApi.download(log.logId);
    } catch (err) {
      reportError(err.message);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div>
      {!compact && (
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <Input label="開始日（から）" type="date" value={from} onChange={(e) => setFrom(e.target.value)} wrapperClassName="w-44" />
          <Input label="開始日（まで）" type="date" value={to} onChange={(e) => setTo(e.target.value)} wrapperClassName="w-44" />
          <Button variant="secondary" onClick={() => setApplied({ from, to })} disabled={loading}>
            <Icon name="filter" className="w-4 h-4" />
            絞り込む
          </Button>
          {(applied.from || applied.to) && (
            <Button variant="ghost" onClick={() => { setFrom(''); setTo(''); setApplied({ from: '', to: '' }); }}>
              解除
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => fetchLogs(null)} disabled={loading} title="再読み込み">
            <Icon name="refresh" className="w-4 h-4" />
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : logs.length === 0 ? (
        <TableEmpty message="フライトログがありません" icon={<Icon name="file" className="w-8 h-8" />} />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>飛行開始（UTC 根拠）</TableHead>
                {!thingName && <TableHead>機体</TableHead>}
                <TableHead>ファイル</TableHead>
                <TableHead>サイズ</TableHead>
                {!compact && <TableHead>PX4</TableHead>}
                <TableHead>投入</TableHead>
                {!compact && <TableHead>登録日時</TableHead>}
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => {
                const src = SOURCE_LABELS[log.source] || SOURCE_LABELS.manual;
                return (
                  <TableRow key={log.logId}>
                    <TableCell>
                      <div className="font-medium">{formatDateTime(log.logStartUtc)}</div>
                      <div className="text-xs text-slate-400">{DATE_SOURCE_LABELS[log.dateSource] || log.dateSource}</div>
                    </TableCell>
                    {!thingName && <TableCell className="font-mono text-xs">{log.thingName}</TableCell>}
                    <TableCell>
                      <span className="font-mono text-xs break-all" title={log.s3Key}>{log.filename}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatSize(log.sizeBytes)}</TableCell>
                    {!compact && <TableCell className="text-xs text-slate-500">{log.ulogInfo?.ver_sw || '-'}</TableCell>}
                    <TableCell>
                      <Badge variant={src.variant} size="sm">{src.label}</Badge>
                      {!compact && log.uploadedBy && <div className="text-xs text-slate-400 mt-0.5">{log.uploadedBy}</div>}
                    </TableCell>
                    {!compact && <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDateTime(log.storedAt || log.uploadedAt)}</TableCell>}
                    <TableCell className="text-right">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownload(log)}
                        loading={downloading === log.logId}
                        title="生の .ulg をダウンロード（Flight Review / PlotJuggler / pyulog でそのまま開けます）"
                      >
                        <Icon name="download" className="w-4 h-4" />
                        {!compact && 'ダウンロード'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {nextToken && (
        <div className="text-center mt-3">
          <Button variant="secondary" size="sm" onClick={() => fetchLogs(nextToken)} loading={loadingMore}>
            さらに読み込む
          </Button>
        </div>
      )}
    </div>
  );
}
